/**
 * Dark/light theme toggle, persisted in localStorage. Matches the previous
 * dashboard's behavior (adds/removes `light` class on `<html>`).
 */
import { useEffect, useState } from 'hono/jsx';

export type Theme = 'dark' | 'light';

function read(): Theme {
  try {
    const v = localStorage.getItem('theme');
    return v === 'light' ? 'light' : 'dark';
  } catch { return 'dark'; }
}

export function applyTheme(theme: Theme): void {
  const html = document.documentElement;
  if (theme === 'light') html.classList.add('light');
  else html.classList.remove('light');
  try { localStorage.setItem('theme', theme); } catch {}
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(read());
  useEffect(() => { applyTheme(theme); }, [theme]);
  return { theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) };
}

/** Run once at boot, before React tree mounts, to avoid a dark-flash on light pages. */
export function bootstrapTheme(): void { applyTheme(read()); }
